const { DataServiceEndPoint } = require("@cloudbreakus/network-utilities")
const { toLoggableIntakeAnswers } = require("../../utils/redaction")

class IntakeQuestions extends DataServiceEndPoint {
  static key = "IntakeQuestions"

  getForInteractionId = async ({ interactionId }) => {
    if (!interactionId) {
      this._logger.debug("[DATA] No interactionId provided to getForInteractionId, returning empty.")
      return { data: [] }
    }
    const url = `${this.url}/v1/interactions/${interactionId}/intake-questions`
    const headers = { Authorization: `bearer ${this.token}` }
    try {
      const result = await this._httpService.get(url, { headers })
      const questions = result?.data || []
      this._logger.debug(`[DATA] Intake Questions fetched from API for interaction ${interactionId}`, {
        questions,
        raw: result,
      })
      return { data: questions }
    } catch (error) {
      this._logger.error(`[DATA] Failed to fetch Intake Questions from API for interaction ${interactionId}`, error)
      throw error
    }
  }

  subscribeCurrentEncounter(fn, { interactionId, onError } = {}) {
    return this._hasuraService.subscribe(
      fn,
      {
        subscriptionQuery: `
          subscription CurrentEncounter($interactionId: uuid!) {
            encounters(
              where: { interactionId: { _eq: $interactionId } }
              order_by: { sequenceNumber: desc }
              limit: 1
            ) {
              id
              sequenceNumber
              status
            }
          }
        `,
        data: { interactionId },
        postProcess: (hasuraData) => hasuraData?.data?.encounters?.[0] ?? null,
      },
      onError
    )
  }

  upsertAnswers = async ({ interactionId, answers, currentEncounterId }) => {
    const url = `${this.url}/v1/interactions/${interactionId}/intake-answers`
    const headers = { Authorization: `bearer ${this.token}` }
    const mode = this._config.get("callsheet.intakeAnswer") || "new"
    const payload = { answers, mode, currentEncounterId }

    return await this._httpService.put(url, payload, { headers })
  }

  // Returns encounter-scoped URL if encounterId is present, or interaction-scoped fallback
  _answersUrl = (interactionId, encounterId) => {
    if (!encounterId) return `${this.url}/v1/interactions/${interactionId}/intake-answers`
    return `${this.url}/v1/interactions/${interactionId}/encounters/${encounterId}/intake-answers`
  }

  getAnswers = async ({ interactionId, encounterId = null, intakeQuestions = [] }) => {
    if (!interactionId) {
      this._logger.debug("[DATA] No interactionId provided to getAnswers, returning empty.")
      return { data: {} }
    }
    const url = this._answersUrl(interactionId, encounterId)
    const headers = { Authorization: `bearer ${this.token}` }
    const result = await this._httpService.get(url, { headers })
    const answers = result?.data || []
    const answersMap = {}

    answers.forEach((item) => {
      answersMap[item.questionId] = item.answer
    })

    const questionsById = new Map(intakeQuestions.map((question) => [question.id, question]))
    const answersWithQuestionDetails = answers.map((item) => ({
      ...item,
      intakeQuestion: questionsById.get(item.questionId),
    }))

    this._logger.debug(
      "[DATA] Intake Answers fetched with question details",
      toLoggableIntakeAnswers(answersWithQuestionDetails)
    )

    return { data: answersMap }
  }

  getCurrentEncounter = async ({ interactionId }) => {
    if (!interactionId) {
      this._logger.debug("[DATA] No interactionId provided to getCurrentEncounter, returning null.")
      return { data: null }
    }
    const url = `${this.url}/v1/interactions/${interactionId}/current-encounter`
    const headers = { Authorization: `bearer ${this.token}` }
    const result = await this._httpService.get(url, { headers })
    const encounter = result?.data || null

    this._logger.debug(`[DATA] Current encounter fetched for interaction ${interactionId}`, encounter)

    return { data: encounter }
  }
  advanceEncounter = async ({ interactionId }) => {
    if (!interactionId) {
      this._logger.debug("[DATA] No interactionId provided to advanceEncounter, returning null.")
      return { data: null }
    }
    const url = `${this.url}/v1/interactions/${interactionId}/advance-encounter`
    const headers = { Authorization: `bearer ${this.token}` }
    const result = await this._httpService.post(url, {}, { headers })
    const encounter = result?.data || null

    this._logger.debug(`[DATA] Encounter advanced for interaction ${interactionId}`, encounter)

    return { data: encounter }
  }
}

module.exports = IntakeQuestions
